<template>
  <div class="dict-data-panel">
    <div class="search-bar">
      <el-input
        v-model="query.dictLabel"
        placeholder="字典标签"
        clearable
        style="width: 160px"
        :disabled="loading"
        @keyup.enter.native="handleQuery"
      />
      <el-select v-model="query.status" placeholder="状态" clearable style="width: 110px" :disabled="loading">
        <el-option label="正常" value="0" />
        <el-option label="停用" value="1" />
      </el-select>
      <el-button type="primary" size="small" icon="el-icon-search" :disabled="loading" @click="handleQuery">搜索</el-button>
      <el-button size="small" icon="el-icon-refresh" :disabled="loading" @click="handleReset">重置</el-button>
      <el-button
        type="primary"
        size="small"
        icon="el-icon-plus"
        style="margin-left: auto"
        :disabled="loading"
        @click="handleAdd"
      >新增字典数据</el-button>
    </div>

    <el-table
      v-loading="loading"
      :data="tableData"
      border
      stripe
      style="width: 100%"
      :header-cell-style="{ textAlign: 'center' }"
      :cell-style="{ textAlign: 'center' }"
    >
      <el-table-column label="排序" prop="dictSort" width="70" />
      <el-table-column label="字典标签" prop="dictLabel" min-width="140" show-overflow-tooltip />
      <el-table-column label="样式属性" prop="listClass" width="120">
        <template slot-scope="{ row }">
          <el-tag v-if="row.listClass" :type="row.listClass" size="small">{{ row.listClass }}</el-tag>
          <span v-else style="color: #ccc">-</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" prop="status" width="80">
        <template slot-scope="{ row }">
          <el-tag :type="row.status === '0' ? 'success' : 'danger'" size="small">
            {{ row.status === '0' ? '正常' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="备注" prop="remark" min-width="120" show-overflow-tooltip />
      <el-table-column label="操作" width="180" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="primary" plain size="mini" :disabled="loading" @click="handleEdit(row)">编辑</el-button>
          <el-button
            v-if="row.status === '0'"
            type="warning"
            plain
            size="mini"
            :disabled="loading"
            @click="handleToggleDataStatus(row)"
          >停用</el-button>
          <el-button
            v-else
            type="success"
            plain
            size="mini"
            :disabled="loading"
            @click="handleToggleDataStatus(row)"
          >启用</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      :current-page="query.pageNo"
      :page-sizes="[10, 20, 50]"
      :page-size="query.pageSize"
      layout="total, sizes, prev, pager, next"
      :total="total"
      style="margin-top: 12px; text-align: right"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
    />

    <!-- 新增/编辑字典数据弹窗 -->
    <DictDataDialog ref="dataDialog" @saved="loadData" />
  </div>
</template>

<script>
// 服务
import { dictService } from './dict.service';
import { clearDictCache } from '@/features/components/dict/dict.api';
// 组件
import DictDataDialog from './dictDataDialog.vue';

export default {
  name: 'DictDataPanel',
  components: { DictDataDialog },

  props: {
    dictType: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      query: {
        pageNo: 1,
        pageSize: 10,
        dictType: '',
        dictLabel: '',
        status: ''
      }
    };
  },

  watch: {
    dictType: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.tableData = [];
          this.total = 0;
          return;
        }
        this.query = {
          pageNo: 1,
          pageSize: 10,
          dictType: val,
          dictLabel: '',
          status: ''
        };
        this.loadData();
      }
    }
  },

  methods: {
    loadData() {
      if (!this.dictType) return;
      this.loading = true;
      dictService.queryDataPage(this.query).then((res) => {
        this.tableData = res.data.rows || [];
        this.total = res.data.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },

    handleQuery() {
      this.query.pageNo = 1;
      this.loadData();
    },

    handleReset() {
      this.query = {
        ...this.query,
        pageNo: 1,
        dictLabel: '',
        status: ''
      };
      this.loadData();
    },

    handleAdd() {
      this.$refs.dataDialog.open(this.dictType, null);
    },

    handleEdit(row) {
      this.$refs.dataDialog.open(this.dictType, row);
    },

    buildDataUpdatePayload(row, status) {
      return {
        dictCode: row.dictCode,
        dictType: row.dictType,
        dictLabel: row.dictLabel,
        dictValue: row.dictValue,
        dictSort: row.dictSort != null ? row.dictSort : 0,
        listClass: row.listClass || '',
        status,
        remark: row.remark || ''
      };
    },

    handleToggleDataStatus(row) {
      const toStop = row.status === '0';
      const tip = toStop
        ? `确认停用「${row.dictLabel}」？停用后业务选项中不再展示该项。`
        : `确认启用「${row.dictLabel}」？`;
      this.$confirm(tip, '提示', { type: 'warning' }).then(() => {
        this.loading = true;
        return dictService.updateData(this.buildDataUpdatePayload(row, toStop ? '1' : '0'));
      }).then(() => {
        clearDictCache(this.dictType);
        this.$message.success(toStop ? '已停用' : '已启用');
        this.loadData();
      }).finally(() => {
        this.loading = false;
      });
    },

    handleSizeChange(size) {
      this.query.pageSize = size;
      this.query.pageNo = 1;
      this.loadData();
    },

    handlePageChange(page) {
      this.query.pageNo = page;
      this.loadData();
    },

    /** 供父级在字典类型变更后刷新条数等 */
    refresh() {
      this.loadData();
    }
  }
};
</script>

<style scoped lang="scss">
.dict-data-panel {
  .search-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
}
</style>
